<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getTemplateService, getStatusIconByRole } from 'src/helpers/xt1_helpers/template_helpers';
	import {
		convertUnits,
		getSubmitConsumptionValue,
		getSubmitPressureValue,
		getSubmitTemperature,
		getUnitByRole,
	} from 'src/helpers/xt1_helpers/template_units_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { deepCloneObj } from 'src/helpers/statistics/core/chart_helper';
	//Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Stores
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import { Text, Select, Button, Input } from '@Shelly/shelly-design-system';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';

	export let device: Device, type: string, role: string;

	let waitForResponse = false;
	let targetLock = false;
	let icon = 'fas fa-calculator';
	const options = [
		{
			title: $format('xt1_template_close', {
				default: 'Close',
			}),
			value: 'close',
		},
		{
			title: $format('xt1_template_open', {
				default: 'Open',
			}),
			value: 'open',
		},
	];
	let value: string = options[0].value;
	let rangeValue: number[] = [0, 0];
	let unitLabel: string = '';

	const onSettingChange = (device: Device) => {
		const templateService = getTemplateService(device);
		if (!templateService?.settings) return;
		const settings = deepCloneObj(templateService.settings);
		const _value = settings[`${type}_action`] || options[0];
		const _range = settings[`${type}_range`] || [0, 0];
		const unit = getUnitByRole(role, templateService);
		_range[0] = convertUnits(_range[0], role, unit);
		_range[1] = convertUnits(_range[1], role, unit);
		icon = getStatusIconByRole(role);
		unitLabel = getUnitByRole(role, templateService, true);
		if (!waitForResponse && !targetLock) {
			if (value !== _value) value = _value;
			if (rangeValue.toString() !== _range.toString()) rangeValue = _range;
		}
		if (targetLock && (value == _value || rangeValue == _range)) {
			targetLock = false;
		}
	};

	const saveValue = async () => {
		waitForResponse = true;
		const _value = value;
		const _rangeValue = rangeValue;
		const unit = getUnitByRole(role, getTemplateService(device));
		const res: SettingRequest = await gen2Request(device, 'Service.SetConfig', {
			id: 0,
			config: {
				[`${type}_action`]: value,
				[`${type}_range`]: /^(water_alarm|alarm_temp)$/.test(type)
					? getSubmitTemperature(rangeValue, unit)
					: /^(water_pressure|alarm_pressure)$/.test(type)
						? getSubmitPressureValue(rangeValue, unit)
						: getSubmitConsumptionValue(rangeValue, unit),
			},
		});
		if (res?.isok) {
			targetLock = true;
			value = _value;
			rangeValue = _rangeValue;
		}
		onSettingsResponse(res, device);
		waitForResponse = false;
	};

	$: settingsStore = getDeviceSettings(device.id);
	$: (onSettingChange(device), $settingsStore);
</script>

<Text styleType="body-small" class="mb-s">
	{$format('sub_settings_title_component_min_value', {
		default: 'Min Value',
	}) + ` (${unitLabel})`}
</Text>
<Input
	aria-label={$format('input_min_value', { default: 'Minimum Value' })}
	size="m"
	class="mb-s"
	maxlength="16"
	type="number"
	{icon}
	value={rangeValue[0].toString()}
	on:input={(e) => (rangeValue[0] = Number(e.target['value']))}
	id="input-ih9eggy"
/>

<Text styleType="body-small" class="mb-s">
	{$format('sub_settings_title_component_max_value', {
		default: 'Max Value',
	}) + ` (${unitLabel})`}
</Text>
<Input
	aria-label={$format('input_max_value', { default: 'Maximum Value' })}
	size="m"
	class="mb-s"
	maxlength="16"
	type="number"
	{icon}
	value={rangeValue[1].toString()}
	on:input={(e) => (rangeValue[1] = Number(e.target['value']))}
	id="input-ru8yzb1"
/>

<Text styleType="body-small" class="mb-s">
	{capFirstChar(
		$format('action', {
			default: 'Action',
		}),
	)}
</Text>
<Select
	aria-label={$format('select_action', { default: 'Select Action' })}
	{options}
	value={options.find((i) => i['value'] == value) || options[0]}
	icon={`fas fa-toggle-${value == 'open' ? 'on' : 'off'}`}
	size="m"
	class="mb-s"
	on:change={(e) => {
		value = e.detail['value'];
	}}
	id="select-97jwg2h"
/>
<Button width="full" size="m" on:click={saveValue} loading={waitForResponse}
	>{$format('button_save', { default: 'Save' })}</Button
>
